/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { KgNode } from './types';
import { symbolKind } from './repoGraphModel';

// Canvas-based node sizing for the GPU renderer: replaces the hidden-DOM
// measure pass (2k invisible divs) with ctx.measureText — deterministic and
// instant. Metrics mirror the retired DOM node exactly: 5px/8px padding,
// 1px border, 1.3 line-height, 11px kind caption over the bucketed label.
const NODE_MAX_W = 220;
const PAD_X = 8;
const PAD_Y = 5;
const BORDER = 1;
const CAPTION_PX = 11;
export const LINE = 1.3;

export type NodeSize = { width: number; height: number };

export const makeNodeMeasurer = (fontFamily: string) => {
  const ctx = document.createElement('canvas').getContext('2d')!;
  return (n: KgNode, labelPx: number, labelWeight: number): NodeSize => {
    ctx.font = `600 ${CAPTION_PX}px ${fontFamily}`;
    const kindW = ctx.measureText(symbolKind(n)).width;
    ctx.font = `${labelWeight} ${labelPx}px ${fontFamily}`;
    const labelW = ctx.measureText(n.label ?? n.id).width;
    const width = Math.min(
      Math.ceil(Math.max(kindW, labelW)) + PAD_X * 2 + BORDER * 2,
      NODE_MAX_W,
    );
    const height =
      Math.round(CAPTION_PX * LINE) +
      Math.round(labelPx * LINE) +
      PAD_Y * 2 +
      BORDER * 2;
    return { width, height };
  };
};

// Ellipsize to a pixel budget with the currently-set ctx font.
export const fitText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return `${text.slice(0, lo)}…`;
};
